import type { StrokeStyle } from "../../utils/svgDiagram2DModel";

interface StrokeStyleControlProps {
    /** Current stroke style id (null/undefined = plain stroke). */
    strokeStyleId: string | null | undefined;
    /** Document-level stroke styles available to style the outline. */
    strokeStyles: StrokeStyle[];
    /** Called with the next stroke style id, or null to clear it (plain stroke). */
    onChange: (strokeStyleId: string | null) => void;
    /**
     * The plain stroke fields (color / width / dash). Rendered only when no
     * style is assigned — a style fully overrides them, so they're hidden while
     * one is active.
     */
    children?: React.ReactNode;
}

/**
 * Inline "Stroke style" picker shared by every standalone-shape inspector
 * (ellipse/rect/polygon/line/arc). Selecting a stroke style draws the shape's
 * outline with that style's stroke — its color/width/dash, parallel lines, and
 * objects-along-path — overriding the shape's own plain stroke. "None" returns
 * to the plain stroke. When a style is active the plain stroke `children` are
 * hidden, with a hint that the style drives the look; when none exist the picker
 * is disabled with a hint.
 */
export function StrokeStyleControl({
    strokeStyleId,
    strokeStyles,
    onChange,
    children,
}: StrokeStyleControlProps) {
    const hasStyles = strokeStyles.length > 0;
    const active = strokeStyleId
        ? strokeStyles.find((s) => s.id === strokeStyleId)
        : undefined;
    // A persisted id that no longer names a known style is treated as cleared.
    const value = active ? active.id : "";

    return (
        <>
            <label className="field-label">
                Stroke style
                <select
                    value={value}
                    disabled={!hasStyles}
                    onChange={(e) =>
                        onChange(e.target.value === "" ? null : e.target.value)
                    }
                >
                    <option value="">None (plain stroke)</option>
                    {strokeStyles.map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.name}
                        </option>
                    ))}
                </select>
            </label>
            {active ? (
                <p
                    className="path-tool-meta"
                    style={{ margin: "4px 0 0", opacity: 0.7 }}
                >
                    The stroke style's color, parallel lines, and objects style
                    the outline.
                </p>
            ) : (
                children
            )}
        </>
    );
}
